import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail, Leaf, Facebook, Linkedin, Twitter, User } from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a41601fb-4bc6-4adf-a1fa-7db9141a0934/artifacts/7yebf3dk_eco-conseil-logo-D0cXQmFM.png";

const navLinks = [
  { href: "/", label: "Accueil" },
  { href: "/simulation", label: "Simulation" },
  { href: "/tarifs", label: "Tarifs" },
  { href: "/a-propos", label: "À propos" },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="Eco-Conseil-Habitat" className="h-14 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-manrope font-medium transition-colors duration-200 ${
                    location.pathname === link.href
                      ? "text-primary"
                      : "text-text-secondary hover:text-primary"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/mon-compte"
                data-testid="nav-mon-compte"
                className={`flex items-center gap-2 font-manrope font-semibold transition-colors duration-200 px-4 py-2 rounded-xl ${
                  location.pathname.startsWith("/mon-compte")
                    ? "bg-emerald-600 text-white"
                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                }`}
              >
                <User size={16} />
                Mon compte
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Link to="/simulation">
                <Button 
                  data-testid="cta-audit-btn"
                  className="btn-primary text-sm"
                >
                  Commencer ma simulation gratuite
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <div className="px-6 py-4 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block py-2 font-manrope font-medium ${
                      location.pathname === link.href
                        ? "text-primary"
                        : "text-text-secondary"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link to="/simulation" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-primary w-full mt-4">
                    Commencer ma simulation gratuite
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-forest-dark text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 inline-block">
                <img src={LOGO_URL} alt="Eco-Conseil-Habitat" className="h-14 w-auto" />
              </div>
              <p className="text-gray-300 font-manrope text-sm leading-relaxed">
                Votre partenaire de confiance pour tous vos projets de rénovation énergétique.
                Accompagnement expert et solutions personnalisées.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors" data-testid="social-facebook">
                  <Facebook size={18} />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors" data-testid="social-linkedin">
                  <Linkedin size={18} />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors" data-testid="social-twitter">
                  <Twitter size={18} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-outfit font-bold text-lg mb-6">Liens rapides</h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-primary-neon transition-colors font-manrope text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-outfit font-bold text-lg mb-6">Nos services</h4>
              <ul className="space-y-3 text-gray-300 font-manrope text-sm">
                <li className="flex items-center gap-2">
                  <Leaf size={14} className="text-primary-neon" />
                  Simulation énergétique
                </li>
                <li className="flex items-center gap-2">
                  <Leaf size={14} className="text-primary-neon" />
                  Audit complet
                </li>
                <li className="flex items-center gap-2">
                  <Leaf size={14} className="text-primary-neon" />
                  Devis personnalisés
                </li>
                <li className="flex items-center gap-2">
                  <Leaf size={14} className="text-primary-neon" />
                  Suivi de projet
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-outfit font-bold text-lg mb-6">Contact</h4>
              <ul className="space-y-4 text-gray-300 font-manrope text-sm">
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-primary-neon flex-shrink-0" />
                  <span>contact-expert@eco-conseil-habitat.com</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm font-manrope">
              © {new Date().getFullYear()} Eco-Conseil-Habitat. Tous droits réservés.
            </p>
            <div className="flex gap-6 text-sm text-gray-400 font-manrope">
              <a href="#" className="hover:text-white transition-colors">Mentions légales</a>
              <a href="#" className="hover:text-white transition-colors">Politique de confidentialité</a>
              <a href="#" className="hover:text-white transition-colors">CGV</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
